import React, { useState } from "react";
import {
  Typography,
  styled,
  Grid2,
  Box,
  MobileStepper,
  IconButton,
} from "@mui/material";
import chunk from "lodash/chunk";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Carousel } from "react-responsive-carousel";
import Ingeborg from "./images/ingeborg.png";
import Konetzke from "./images/konetzke.png";
import Siebers from "./images/siebers.png";
import Willkomm from "./images/willkomm.png";
import "react-responsive-carousel/lib/styles/carousel.min.css";

const CarouselStyled = styled(Carousel)({
  "& .slide": {
    background: "transparent",
  },
});

const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  width: 36,
  height: 36,
  border: `1px solid ${theme.palette.border.secondary}`,
  borderRadius: 24,
}));

const ContainerStyled = styled(Box)(({ theme }) => ({}));

const StepperStyled = styled(MobileStepper)(({ theme }) => ({
  border: "none !important",
  boxShadow: "none !important",
}));

const CardContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: theme.spacing(4),
  padding: theme.spacing(5, 8),
  background: theme.palette.background.subtle,
  width: "100%",
  minHeight: 256,
  borderRadius: 8,
  backgroundImage: 'url("/images/redesign/cashback/background-waves.svg")',
  backgroundPosition: "left top",
  backgroundRepeat: "no-repeat",
}));

const CardMeta = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(4),
}));

const CardMetaImage = styled("img")({
  width: "56px !important",
  height: "56px !important",
  borderRadius: "50%",
});

const CardText = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  lineHeight: "26px",
  textAlign: "left",
}));

const TestimonialMap: Record<string, CardProps> = {
  vincentWillkomm: {
    imgPath: Willkomm,
    title: i18n.t("cashbackWizard.start.cards.vincentWillkomm.name"),
    text: i18n.t("cashbackWizard.start.cards.vincentWillkomm.text"),
    profession: i18n.t("cashbackWizard.start.cards.vincentWillkomm.profession"),
  },
  ingeborgK: {
    imgPath: Ingeborg,
    title: i18n.t("cashbackWizard.start.cards.ingeborgK.name"),
    text: i18n.t("cashbackWizard.start.cards.ingeborgK.text"),
    profession: i18n.t("cashbackWizard.start.cards.ingeborgK.profession"),
  },
  konetzke: {
    imgPath: Konetzke,
    title: i18n.t("cashbackWizard.start.cards.konetzke.name"),
    text: i18n.t("cashbackWizard.start.cards.konetzke.text"),
    profession: i18n.t("cashbackWizard.start.cards.konetzke.profession"),
  },
  siebers: {
    imgPath: Siebers,
    title: i18n.t("cashbackWizard.start.cards.siebers.name"),
    text: i18n.t("cashbackWizard.start.cards.siebers.text"),
    profession: i18n.t("cashbackWizard.start.cards.siebers.profession"),
  },
};

const testimonialSources = chunk(Object.keys(TestimonialMap), 2);

interface CardProps {
  imgPath: string;
  title: string;
  profession: string;
  text: string;
}

export const Testimonials = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleNextClick = () => setCurrentSlide((state) => state + 1);
  const handlePreviousClick = () => setCurrentSlide((state) => state - 1);
  const handleChangeCarousel = (index: number) => {
    if (currentSlide !== index) {
      setCurrentSlide(index);
    }
  };
  return (
    <ContainerStyled data-sectionid="testimonials">
      <Typography variant="h3" component="h2">
        {t("cashbackWizard.start.cards.title")}
      </Typography>
      <Typography variant="h3" component="h2" mb={11} color="textSecondary">
        {t("cashbackWizard.start.cards.subtitle")}
      </Typography>
      <CarouselStyled
        showThumbs={false}
        showArrows={false}
        showStatus={false}
        showIndicators={false}
        selectedItem={currentSlide}
        autoPlay
        infiniteLoop
        interval={5000}
        onChange={handleChangeCarousel}
      >
        {testimonialSources.map((testimonialSource) => (
          <Grid2 container spacing={4} key={testimonialSource[0]}>
            {testimonialSource.map((source) => (
              <Grid2
                key={source}
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <CardContainer>
                  <CardText>{TestimonialMap[source].text}</CardText>
                  <CardMeta>
                    <CardMetaImage src={TestimonialMap[source].imgPath} />
                    <div>
                      <Typography variant="subtitle1">
                        {TestimonialMap[source].title}
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        {TestimonialMap[source].profession}
                      </Typography>
                    </div>
                  </CardMeta>
                </CardContainer>
              </Grid2>
            ))}
          </Grid2>
        ))}
      </CarouselStyled>
      {testimonialSources.length > 1 && (
        <Box mt={8}>
          <Grid2 container justifyContent="center">
            <Grid2
              size={{
                xs: 12,
                sm: 6,
                md: 4,
              }}
            >
              <StepperStyled
                variant="dots"
                position="static"
                activeStep={currentSlide}
                steps={testimonialSources.length}
                backButton={
                  <IconButtonStyled onClick={handlePreviousClick}>
                    <ChevronLeftRoundedIcon />
                  </IconButtonStyled>
                }
                nextButton={
                  <IconButtonStyled onClick={handleNextClick}>
                    <ChevronRightRoundedIcon />
                  </IconButtonStyled>
                }
              />
            </Grid2>
          </Grid2>
        </Box>
      )}
    </ContainerStyled>
  );
};
